import {
  colorPaletteOutline,
  flag,
  home,
  homeOutline,
  pushOutline,
} from 'ionicons/icons';
import { Scratchpad } from '../../../../../api/graphql/generated';
import { IntercomArticle } from '../../../../../config/intercom';
import i18n from '../../../../../i18n';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { FanucModel } from '../../../../../robot/manufacturers/fanuc/fanuc.types';
import { QNear, Waypoint } from '../../../../../util/machine-variable.utils';
import { moveItem } from '../../../shared/component/icon/move-item';
import { reportProduction } from '../../../shared/component/icon/report-production';
import { waitForOperator } from '../../../shared/component/icon/wait-for-operator';
import {
  Flow,
  FlowItemMetadata,
  FlowItemParameterMetadata,
  FlowRule,
  RuleValidationResponse,
  TeachIOMapping,
  Tool,
  ToolConfig,
  WaypointParameterValue,
  WidgetType,
} from '../../../shared/model/flow.types';
import { flowRuleCheckContiguousByType } from '../../../shared/util/flow-rule.util';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace BeaconPaintingData {
  export const APP_NAME = 'Beacon Painting';
  export const ANALYTICS_PREFIX = 'Beacon Painting';
  export const APP_SLUG = 'beacon_painting';

  export enum MachinePreference {
    PART_CONFIG_DEFAULTS = 'beacon_painting.part_config.defaults',
  }

  export enum Painter {
    NORDSON_ENCORE_GEN3 = 'nordson_encore_gen3',
  }

  export enum PaintTypeEnum {
    LINEAR = 1,
    RADIAL = 2,
  }

  export enum WaitForInputType {
    OPERATOR_BUTTON = 'OPERATOR_BUTTON',
    SENSOR = 'SENSOR',
    TIME = 'TIME',
  }

  export enum WaitForInputPort {
    RED = 16,
    BLUE = 17,
    YELLOW = 18,
    GREEN = 19,
  }

  export enum TriggerOutputPort {
    RED = 24,
    BLUE = 25,
    YELLOW = 26,
    GREEN = 27,
  }

  export interface FlowConfig {
    home: {
      waypoint: Waypoint;
      qnear: QNear;
    };
    ioMapping: TeachIOMapping;
    applicatorTool: ToolConfig;
    applicator: {
      model: string;
      ioMap: {
        mode: string;
        trigger: string;
      };
    };
    betweenSpeed: number;
    startAtId?: string;
    stepModeEnabled?: boolean;
  }

  export interface PaintingScratchpad extends Scratchpad {
    appData: Flow<FlowConfig>;
  }

  export interface License {
    painter: string;
  }

  export enum FlowItemRef {
    PAINT = 'paint',
    END = 'end',
    MOVE = 'move',
    MOVE_HOME = 'move to home',
    PRODUCTION_REPORTING = 'report production',
    SECTION = 'section',
    START = 'start',
    TRIGGER_OUTPUT = 'trigger output',
    WAIT_FOR = 'wait for operator',
  }

  export enum FlowItemParameterRef {
    APPROACH = 'approach',
    DEPART = 'depart',
    MOVE_MOTION_TYPE = 'moveMotionType',
    MOVE_SPEED_ADJUSTMENT_RATIO = 'moveSpeedAdjustmentRatio',
    MOVE_TRAVEL_SPEED = 'moveTravelSpeed',
    MOVE_BLEND_RADIUS = 'moveBlendRadius',
    PAINT_TYPE = 'paintType',
    POINT = 'point',
    /**
     * Powder flow rate percentage (0-100).
     * Controls the amount of powder being sprayed.
     */
    POWDER = 'powder',
    /**
     * Air flow rate percentage (0-100).
     * Controls the amount of air being used.
     */
    AIR = 'air',
    /**
     * Kilovolts (kV) measure the strength of the electrical charge pushing the paint/powder onto the part.
     * Controls the intensity of the electrostatic force.
     */
    KV = 'kv',
    /**
     * Microamps (uA) measure the current or flow of the kV electrical charge.
     * Controls the amount of charge/paint transfer.
     */
    UA = 'ua',
    PRODUCTION_QUANTITY = 'productionQuanity',
    QNEAR = 'qnear',
    SECTION_COLOR = 'sectionColor',
    TRAVEL_SPEED = 'travelSpeed',
    TRIGGER_OUTPUT_PORT = 'triggerOutputPort',
    TRIGGER_OUTPUT_STATE = 'triggerOutputState',
    WAIT_FOR_OPERATOR_MESSAGE = 'operatorMessage',
    WAIT_FOR_INPUT = 'waitForInput',
    WAIT_FOR_TIME = 'waitForTime',
    WAIT_FOR_INPUT_STATE = 'waitForInputState',
    WAIT_FOR_TYPE_OF_INPUT = 'typeOfInput',
    WAYPOINT = 'waypoint',
  }

  export enum FlowItemParameterTag {
    APPROACH = 'approach',
    DEPART = 'depart',
    WAIT_FOR_INPUT = 'waitForInput',
    WAIT_FOR_TIME = 'waitForTime',
    WAIT_FOR_OPERATOR = 'waitForOperator',
  }

  export const MoveMotionTypeMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.MOVE_MOTION_TYPE,
      apiName: 'motionType',
      name: 'painting:motion_type',
      value: {
        type: 'number',
        default: 0,
      },
      displayOrder: 1,
      ui: {
        widget: WidgetType.HIDDEN,
      },
    };

  export const MoveSpeedAdjustmentRatioMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.MOVE_SPEED_ADJUSTMENT_RATIO,
    apiName: 'moveSpeedAdjustmentRatio',
    name: 'painting:move_speed_adjustment_ratio',
    displayOrder: 10,
    description: 'painting:move_speed_adjustment_ratio_desc',
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 0.25,
      },
    },
    value: {
      type: 'number',
      required: true,
      minimum: 0.25,
      maximum: 1,
      default: 1,
    },
  };

  export const MoveTravelSpeedMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.MOVE_TRAVEL_SPEED,
      apiName: 'travelSpeed',
      name: 'painting:travel_speed',
      value: {
        type: 'number',
        default: 0.03,
      },
    };

  export const MoveBlendRadiusMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.MOVE_BLEND_RADIUS,
      apiName: 'blendRadius',
      displayOrder: 10,
      name: 'painting:blend_radius',
      value: {
        type: 'number',
        default: 50,
        minimum: 0,
        maximum: 100,
        required: true,
      },
      ui: {
        widget: WidgetType.HIDDEN,
      },
    };

  export const QNearMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.QNEAR,
      apiName: 'qnear',
      value: {
        type: 'array',
        required: true,
      },
    };

  export const WaypointMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAYPOINT,
      apiName: 'waypoint',
      name: 'painting:waypoint',
      value: WaypointParameterValue,
    };

  export const ProductionQuanityMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.PRODUCTION_QUANTITY,
      apiName: 'quantity',
      displayOrder: 0,
      name: 'common:quantity',
      value: {
        type: 'number',
        default: 1,
        required: true,
      },
      ui: {
        widget: WidgetType.NUMBER,
      },
    };

  export const TriggerOutputPortMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.TRIGGER_OUTPUT_PORT,
      apiName: 'outputPort',
      displayOrder: 0,
      name: 'painting:trigger_output',
      value: {
        type: 'number',
        default: TriggerOutputPort.RED,
        required: true,
      },
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'common:color_red',
            value: TriggerOutputPort.RED,
          },
          {
            label: 'common:color_blue',
            value: TriggerOutputPort.BLUE,
          },
          {
            label: 'common:color_yellow',
            value: TriggerOutputPort.YELLOW,
          },
          {
            label: 'common:color_green',
            value: TriggerOutputPort.GREEN,
          },
        ],
      },
    };

  export const TriggerOutputStateMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.TRIGGER_OUTPUT_STATE,
      apiName: 'outputState',
      displayOrder: 0,
      name: 'common:state',
      value: {
        type: 'boolean',
        default: true,
      },
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'common:on',
            value: true,
          },
          {
            label: 'common:off',
            value: false,
          },
        ],
      },
    };

  export const WaitForOperatorMessageMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAIT_FOR_OPERATOR_MESSAGE,
      apiName: 'message',
      tags: [FlowItemParameterTag.WAIT_FOR_OPERATOR],
      displayOrder: 100,
      name: 'common:message',
      value: {
        type: 'string',
        default: i18n.t('painting:wait_for_operator_default_message'),
        required: false,
      },
      ui: {
        widget: WidgetType.TEXT,
      },
    };

  export const WaitForTypeMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT,
      apiName: 'typeOfInput',
      displayOrder: 0,
      name: 'painting:wait_for',
      value: {
        type: 'string',
        default: WaitForInputType.OPERATOR_BUTTON,
        required: false,
      },
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'common:operator',
            value: WaitForInputType.OPERATOR_BUTTON,
          },
          {
            label: 'common:input',
            value: WaitForInputType.SENSOR,
          },
          {
            label: 'common:time',
            value: WaitForInputType.TIME,
          },
        ],
      },
    };

  export const WaitForInputMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAIT_FOR_INPUT,
      tags: [FlowItemParameterTag.WAIT_FOR_INPUT],
      apiName: 'inputPort',
      displayOrder: 0,
      name: 'common:input',
      value: {
        type: 'number',
        default: WaitForInputPort.RED,
        required: true,
      },
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'common:color_red',
            value: WaitForInputPort.RED,
          },
          {
            label: 'common:color_blue',
            value: WaitForInputPort.BLUE,
          },
          {
            label: 'common:color_yellow',
            value: WaitForInputPort.YELLOW,
          },
          {
            label: 'common:color_green',
            value: WaitForInputPort.GREEN,
          },
        ],
      },
    };

  export const WaitForInputStateMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAIT_FOR_INPUT_STATE,
      tags: [FlowItemParameterTag.WAIT_FOR_INPUT],
      apiName: 'waitForState',
      displayOrder: 0,
      name: 'common:state',
      value: {
        type: 'boolean',
        default: true,
      },
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'common:on',
            value: true,
          },
          {
            label: 'common:off',
            value: false,
          },
        ],
      },
    };

  export const WaitForTimeMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAIT_FOR_TIME,
      apiName: 'inputPort',
      tags: [FlowItemParameterTag.WAIT_FOR_TIME],
      displayOrder: 0,
      name: 'common:time_seconds',
      value: {
        type: 'number',
        default: 5,
      },
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.01,
        },
      },
    };

  export const SectionColorParameterMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.SECTION_COLOR,
      name: 'common:color',
      displayOrder: 10,
      apiName: 'color',
      value: {
        type: 'string',
      },
      ui: {
        widget: WidgetType.COLOR_PICKER,
      },
    };

  export const ApproachMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.APPROACH,
      apiName: 'approach',
      name: 'painting:approach_paint',
      value: {
        type: 'boolean',
        default: false,
      },
    };

  export const DepartMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.DEPART,
      apiName: 'depart',
      name: 'painting:depart_paint',
      value: {
        type: 'boolean',
        default: false,
      },
    };

  export const PaintTypeMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.PAINT_TYPE,
      apiName: 'motionType',
      name: 'painting:paint_type',
      tags: [FlowItemParameterTag.APPROACH, FlowItemParameterTag.DEPART],
      description: 'painting:paint_type_desc',
      displayOrder: 1,
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          { label: 'painting:paint_type_linear', value: PaintTypeEnum.LINEAR },
          { label: 'painting:paint_type_radial', value: PaintTypeEnum.RADIAL },
        ],
      },
      value: {
        type: 'number',
        default: 1,
      },
    };

  export const TravelSpeedMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.TRAVEL_SPEED,
      apiName: 'travelSpeed',
      name: 'painting:paint_speed',
      tags: [FlowItemParameterTag.APPROACH],
      displayOrder: 25,
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.1,
        },
      },
      value: {
        type: 'number',
        required: true,
        default: 84.67, // 200 ipm
        minimum: 1.17, // 50 ipm
        maximum: 317.5, // 750 ipm
        uom: {
          target: 'mm/s',
          system: {
            imperial: 'in/min',
            metric: 'cm/min',
          },
          precision: 3,
        },
      },
    };

  export const PowderMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.POWDER,
      apiName: 'powder',
      name: 'painting:powder_flow_rate',
      tags: [FlowItemParameterTag.APPROACH],
      displayOrder: 50,
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 1,
        },
      },
      value: {
        type: 'number',
        required: true,
        default: 50,
        minimum: 0,
        maximum: 100,
      },
    };

  export const AirMetadata: FlowItemParameterMetadata<FlowItemParameterTag> = {
    ref: FlowItemParameterRef.AIR,
    apiName: 'air',
    name: 'painting:air_flow_rate',
    tags: [FlowItemParameterTag.APPROACH],
    displayOrder: 60,
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 1,
      },
    },
    value: {
      type: 'number',
      required: true,
      default: 50,
      minimum: 0,
      maximum: 100,
    },
  };

  export const KvMetadata: FlowItemParameterMetadata<FlowItemParameterTag> = {
    ref: FlowItemParameterRef.KV,
    apiName: 'kv',
    name: 'painting:electrostatic_charge',
    tags: [FlowItemParameterTag.APPROACH],
    displayOrder: 70,
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 1,
      },
    },
    value: {
      type: 'number',
      required: true,
      default: 50,
      minimum: 0,
      maximum: 100,
    },
  };

  export const UaMetadata: FlowItemParameterMetadata<FlowItemParameterTag> = {
    ref: FlowItemParameterRef.UA,
    apiName: 'ua',
    name: 'painting:current_limit',
    tags: [FlowItemParameterTag.APPROACH],
    displayOrder: 80,
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 1,
      },
    },
    value: {
      type: 'number',
      required: true,
      default: 15,
      minimum: 0,
      maximum: 100,
    },
  };

  export const FlowItemPaintParametersMetadata: FlowItemParameterMetadata<FlowItemParameterTag>[] =
    [
      PaintTypeMetadata,
      ApproachMetadata,
      DepartMetadata,
      QNearMetadata,
      TravelSpeedMetadata,
      PowderMetadata,
      AirMetadata,
      KvMetadata,
      UaMetadata,
      WaypointMetadata,
    ];

  export const FlowItemMoveParametersMetadata: FlowItemParameterMetadata<FlowItemParameterTag>[] =
    [
      MoveBlendRadiusMetadata,
      MoveMotionTypeMetadata,
      MoveSpeedAdjustmentRatioMetadata,
      MoveTravelSpeedMetadata,
      QNearMetadata,
      WaypointMetadata,
    ];

  export const FlowItemWaitForParametersMetadata: FlowItemParameterMetadata<FlowItemParameterTag>[] =
    [
      WaitForTypeMetadata,
      WaitForInputMetadata,
      WaitForTimeMetadata,
      WaitForOperatorMessageMetadata,
      WaitForInputStateMetadata,
    ];

  export const FlowItemTriggerOutputParametersMetadata: FlowItemParameterMetadata<FlowItemParameterTag>[] =
    [TriggerOutputPortMetadata, TriggerOutputStateMetadata];

  export const FlowItemSectionParametersMetadata: FlowItemParameterMetadata<FlowItemParameterTag>[] =
    [SectionColorParameterMetadata];

  export const BeaconStartItem: FlowItemMetadata = {
    ref: FlowItemRef.START,
    codeBlock: 0,
    name: 'common:start',
    description: 'painting:start_desc',
    icon: home,
    displayOrder: 0,
    availableParameters: [],
  };

  export const BeaconEndItem: FlowItemMetadata = {
    ref: FlowItemRef.END,
    codeBlock: 10000,
    name: 'common:end',
    description: 'painting:end_desc',
    icon: flag,
    displayOrder: 10000,
    availableParameters: [],
  };

  export const BeaconSectionItem: FlowItemMetadata = {
    ref: FlowItemRef.SECTION,
    codeBlock: 6,
    name: 'common:section',
    description: 'painting:section_desc',
    icon: flag,
    displayOrder: 10000,
    availableParameters: [...FlowItemSectionParametersMetadata],
    hidden: true,
  };

  export const BeaconMoveItem: FlowItemMetadata = {
    ref: FlowItemRef.MOVE,
    codeBlock: 2,
    name: 'painting:move',
    description: 'painting:move_desc',
    icon: moveItem,
    displayOrder: 20,
    availableParameters: [...FlowItemMoveParametersMetadata],
  };

  export const BeaconPaintItem: FlowItemMetadata = {
    ref: FlowItemRef.PAINT,
    codeBlock: 8,
    name: 'painting:paint',
    description: 'painting:paint_desc',
    icon: colorPaletteOutline,
    displayOrder: 10,
    availableParameters: [...FlowItemPaintParametersMetadata],
  };

  export const BeaconMoveHomeItem: FlowItemMetadata = {
    ref: FlowItemRef.MOVE_HOME,
    codeBlock: 2,
    name: 'painting:move_home',
    description: 'painting:move_home_desc',
    icon: homeOutline,
    displayOrder: 30,
    availableParameters: [],
  };

  export const BeaconWaitForItem: FlowItemMetadata = {
    ref: FlowItemRef.WAIT_FOR,
    codeBlock: 3,
    name: 'painting:wait_for',
    description: 'painting:wait_for_desc',
    icon: waitForOperator,
    displayOrder: 40,
    availableParameters: [...FlowItemWaitForParametersMetadata],
  };

  export const BeaconReportProductionItem: FlowItemMetadata = {
    ref: FlowItemRef.PRODUCTION_REPORTING,
    codeBlock: 4,
    name: 'painting:production_reporting',
    description: 'painting:production_reporting_desc',
    icon: reportProduction,
    displayOrder: 50,
    availableParameters: [ProductionQuanityMetadata],
  };

  export const BeaconTriggerOutputItem: FlowItemMetadata = {
    ref: FlowItemRef.TRIGGER_OUTPUT,
    codeBlock: 5,
    name: 'painting:trigger_output',
    description: 'painting:trigger_output_desc',
    icon: pushOutline,
    displayOrder: 60,
    availableParameters: FlowItemTriggerOutputParametersMetadata,
    articleId: IntercomArticle.INTEGRATE_WITH_EXTERNAL_EQUIPMENT,
  };

  export const BeaconFlowItemsMetadata = [
    BeaconPaintItem,
    BeaconMoveItem,
    BeaconMoveHomeItem,
    BeaconReportProductionItem,
    BeaconSectionItem,
    BeaconWaitForItem,
  ];

  export const CommonItems: FlowItemMetadata[] = [
    BeaconMoveItem,
    BeaconMoveHomeItem,
    BeaconReportProductionItem,
    BeaconSectionItem,
    BeaconWaitForItem,
  ];

  type FlowItemByPainterMapping = {
    [key in Painter]: FlowItemMetadata[];
  };

  export const FlowItemsByPainter: FlowItemByPainterMapping = {
    [Painter.NORDSON_ENCORE_GEN3]: BeaconFlowItemsMetadata,
  };

  export enum ToolKey {
    NORDSON_ENCORE_GEN3 = 'nordson_encore_gen3',
  }

  export const Tools: Tool[] = [
    {
      id: ToolKey.NORDSON_ENCORE_GEN3,
      label: 'Nordson Encore Gen3',
      config: {
        name: ToolKey.NORDSON_ENCORE_GEN3,
        cog: [0.039, -0.085, 0.12],
        inertia: [0.0238, 0.0101, 0.0135, 0, 0, 0],
        mass: 1.7,
        tcp: {
          rx: 0,
          ry: 0.261,
          rz: 0,
          x: 0.521,
          y: 0,
          z: 0.08,
        },
      },
      equipment: [Painter.NORDSON_ENCORE_GEN3],
      models: [FanucModel.CRX10IA_LP],
    },
  ];

  export interface GetParameterMetadataFromFormOptions {
    forceDepart?: boolean;
    forceApproach?: boolean;
    forceIncludeTags?: FlowItemParameterTag[];
    forceExcludeRefs?: FlowItemParameterRef[];
  }

  export const DefaultPaintParameterMetadataFromFormOptions: GetParameterMetadataFromFormOptions =
    {
      forceIncludeTags: [
        FlowItemParameterTag.DEPART,
        FlowItemParameterTag.APPROACH,
      ],
      forceExcludeRefs: [
        FlowItemParameterRef.PAINT_TYPE,
        FlowItemParameterRef.QNEAR,
        FlowItemParameterRef.WAYPOINT,
      ],
      forceApproach: false,
      forceDepart: false,
    };

  const RadialPaintContiguousRule: FlowRule = {
    execute: (flow: Flow): RuleValidationResponse | undefined => {
      return flowRuleCheckContiguousByType({
        flow,
        itemTypeRef: FlowItemRef.PAINT,
        parameterTypeRef: FlowItemParameterRef.PAINT_TYPE,
        minRequired: 3,
        primaryTypes: [PaintTypeEnum.RADIAL],
        followedByTypes: [PaintTypeEnum.LINEAR, PaintTypeEnum.RADIAL],
        followedByStartOffset: 2,
        suffixMessage: getLocalizedText(
          'painting:more_radial_points_require'
        ) as string,
      });
    },
  };

  const LinearPaintContiguousRule: FlowRule = {
    execute: (flow: Flow): RuleValidationResponse | undefined => {
      return flowRuleCheckContiguousByType({
        flow,
        itemTypeRef: FlowItemRef.PAINT,
        parameterTypeRef: FlowItemParameterRef.PAINT_TYPE,
        minRequired: 2,
        primaryTypes: [PaintTypeEnum.LINEAR],
        suffixMessage: getLocalizedText(
          'painting:more_linear_point_required'
        ) as string,
        precededByTypes: [PaintTypeEnum.RADIAL],
        followedByTypes: [PaintTypeEnum.LINEAR, PaintTypeEnum.RADIAL],
      });
    },
  };

  export const FlowValidationRules = [
    LinearPaintContiguousRule,
    RadialPaintContiguousRule,
  ];

  export enum FlowSectionType {}
}
